// src/components/AdminReportsModal.js
import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

const TYPE_LABEL = {
  'episode': 'Nefunguje epizoda',
  'av': 'Zvuk a video',
  'subs': 'Titulky',
};

export default function AdminReportsModal({ onClose }) {
  const { role } = useAuth();
  const allowed = role === 'Majitel' || role === 'Admin';
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [confirmItem, setConfirmItem] = React.useState(null);
  const [roleFilter, setRoleFilter] = React.useState('all'); // all | User | VIP | Moderátor | Admin | Majitel
  const [kindFilter, setKindFilter] = React.useState('all'); // all | serial | film
  const [roleOpen, setRoleOpen] = React.useState(false);
  const [kindOpen, setKindOpen] = React.useState(false);
  const roleRef = React.useRef(null);
  const kindRef = React.useRef(null);
  const LIMIT = 6;

  React.useEffect(() => {
    const onDoc = (e) => {
      try {
        if (roleRef.current && !roleRef.current.contains(e.target)) setRoleOpen(false);
        if (kindRef.current && !kindRef.current.contains(e.target)) setKindOpen(false);
      } catch {}
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, []);

  const load = async (reset=false) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/reports?limit=${LIMIT}&offset=${reset?0:offset}&_=${Date.now()}`, { cache: 'no-store' });
      const data = await r.json();
      const list = Array.isArray(data?.items) ? data.items : [];
      setTotal(Number(data?.total) || 0);
      if (reset) {
        setItems(list);
        setOffset(list.length);
      } else {
        setItems(prev => prev.concat(list.filter(it => !prev.some(p => p.id === it.id))));
        setOffset(prev => prev + list.length);
      }
    } catch (_) {
      // ignore
    } finally { setBusy(false); }
  };

  React.useEffect(() => { load(true); }, []);

  const del = (id) => { if (id) setConfirmItem(items.find(x => x.id === id) || null); };

  const performDelete = async () => {
    const id = confirmItem?.id;
    if (!id) { setConfirmItem(null); return; }
    try {
      const r = await fetch(`/api/reports/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (r.ok) setItems(prev => prev.filter(x => x.id !== id));
    } catch (_) {}
    setConfirmItem(null);
  };

  const close = () => onClose && onClose();

  const content = (
    <Overlay onClick={close}>
      <Modal role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Nahlášení</Title>
          <Close onClick={close} aria-label="Zavřít">✕</Close>
        </Header>
        {!allowed ? (
          <Info>Pouze Admin/Majitel vidí nahlášení.</Info>
        ) : (
          <>
            <Info>Novější nahoře. Celkem: {total}</Info>
            <ControlsRow>
              <Dropdown ref={roleRef}>
                <DropTrigger onClick={() => { setRoleOpen(v=>!v); setKindOpen(false); }} aria-expanded={roleOpen}>Role: {roleFilter === 'all' ? 'Vše' : roleLabel(roleFilter)}</DropTrigger>
                {roleOpen && (
                  <DropMenu>
                    {['all','User','VIP','Moderátor','Admin','Majitel'].map(val => (
                      <DropItem key={val} onClick={() => { setRoleFilter(val); setRoleOpen(false); }}>
                        {val === 'all' ? 'Vše' : roleLabel(val)}
                      </DropItem>
                    ))}
                  </DropMenu>
                )}
              </Dropdown>

              <Dropdown ref={kindRef}>
                <DropTrigger onClick={() => { setKindOpen(v=>!v); setRoleOpen(false); }} aria-expanded={kindOpen}>Typ: {kindFilter === 'all' ? 'Vše' : (kindFilter === 'serial' ? 'Seriál' : 'Film')}</DropTrigger>
                {kindOpen && (
                  <DropMenu>
                    {[{v:'all',t:'Vše'},{v:'serial',t:'Seriál'},{v:'film',t:'Film'}].map(o => (
                      <DropItem key={o.v} onClick={() => { setKindFilter(o.v); setKindOpen(false); }}>{o.t}</DropItem>
                    ))}
                  </DropMenu>
                )}
              </Dropdown>
            </ControlsRow>
            <List>
              {(() => {
                const filtered = (items || []).filter(it => {
                  if (roleFilter !== 'all' && it.role !== roleFilter) return false;
                  if (kindFilter !== 'all') {
                    const pm = it.playMeta || {};
                    const kind = pm.contentType || ((pm.season != null || pm.index != null) ? 'serial' : 'film');
                    if (kind !== kindFilter) return false;
                  }
                  return true;
                });
                if (!filtered.length) return <Empty>Žádná nahlášení.</Empty>;
                return filtered.map((it) => (
                <Row key={it.id}>
                  <Col>
                    <Type>{TYPE_LABEL[it.type] || it.type}</Type>
                    <Meta>
                      <span>{new Date(Number(it.createdAt)||0).toLocaleString('cs-CZ')}</span>
                      {it.userName && <span> • {it.userName}</span>}
                      {it.role && <RoleBadge color={roleColor(it.role)}>{roleLabel(it.role)}</RoleBadge>}
                    </Meta>
                    {it.playMeta && (
                      <Small>
                        {it.playMeta.showTitle ? (<><strong>Seriál:</strong> {it.playMeta.showTitle} </>) : null}
                        {(it.playMeta.season != null || it.playMeta.index != null) && (
                          <>
                            {it.playMeta.season != null && <>• <strong>Řada:</strong> {String(it.playMeta.season)}</>}
                            {it.playMeta.index != null && <> • <strong>Epizoda:</strong> {String(it.playMeta.index)}{it.playMeta.epTitle ? ` – ${it.playMeta.epTitle}` : ''}</>}
                          </>
                        )}
                      </Small>
                    )}
                    {it.pagePath && (
                      <Small>
                        <OpenBtn type="button" onClick={() => window.open(it.pagePath, '_blank', 'noopener,noreferrer')}>Otevřít zdroj</OpenBtn>
                      </Small>
                    )}
                  </Col>
                  {it.playMeta && it.playMeta.img ? (
                    <Thumb src={it.playMeta.img} alt="thumb" />
                  ) : (
                    <ThumbPlaceholder />
                  )}
                  <Actions>
                    <Btn onClick={() => del(it.id)}>Smazat</Btn>
                  </Actions>
                </Row>
              ));
              })()}
            </List>
            {offset < total && (
              <LoadMore onClick={() => load(false)} disabled={busy}>{busy ? 'Načítám…' : 'Načíst další'}</LoadMore>
            )}
          </>
        )}
      </Modal>
      {confirmItem && (
        <ConfirmOverlay onClick={() => setConfirmItem(null)}>
          <ConfirmBox role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <ConfirmTitle>Opravdu smazat nahlášení?</ConfirmTitle>
            <ConfirmText>
              Toto nahlášení bude trvale odstraněno.
              <br />
              <strong>{TYPE_LABEL[confirmItem.type] || confirmItem.type}</strong>
            </ConfirmText>
            <ConfirmActions>
              <CancelBtn type="button" onClick={() => setConfirmItem(null)}>Zrušit</CancelBtn>
              <DangerBtn type="button" onClick={performDelete}>Smazat</DangerBtn>
            </ConfirmActions>
          </ConfirmBox>
        </ConfirmOverlay>
      )}
    </Overlay>
  );

  const target = typeof document !== 'undefined' ? document.body : null;
  return target ? ReactDOM.createPortal(content, target) : content;
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10,10,12,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
`;

const Modal = styled.div`
  width: min(900px, 96vw);
  background: linear-gradient(180deg,#0f1720,#11131a);
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(2,6,23,0.6);
  padding: 18px;
  color: #e6eef8;
  border: 1px solid rgba(255,255,255,0.06);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
`;

const Close = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: #e6eef8;
  font-size: 18px;
  cursor: pointer;
`;

const Info = styled.div`
  opacity: .9;
  margin-bottom: 10px;
`;

const ControlsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;
const Dropdown = styled.div`
  position: relative;
  user-select: none;
`;
const DropTrigger = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.06);
  color: #e6eef8;
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 10px;
  height: 36px;
  padding: 0 12px;
  cursor: pointer;
  min-width: 160px;
  &:hover { border-color: rgba(255,255,255,0.25); }
`;
const DropMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: linear-gradient(180deg,#0f1720,#11131a);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(2,6,23,0.6);
  min-width: 200px;
  padding: 6px;
  z-index: 5;
`;
const DropItem = styled.button`
  appearance: none;
  width: 100%;
  text-align: left;
  background: transparent;
  color: #e6eef8;
  border: 0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.08); }
`;

const List = styled.div`
  display: grid;
  gap: 10px;
  max-height: 60vh;
  overflow: auto;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px auto;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  background: #2f2f2f;
  border: 1px solid #444;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px; /* trošku větší rozestup mezi texty */
`;

const Type = styled.div`
  font-weight: 600;
`;

const Meta = styled.div`
  opacity: .9;
`;

const Role = styled.span`
  opacity: .8;
  margin-left: 4px;
`;

const Small = styled.div`
  font-size: 13px;
  opacity: .9;
`;

const Thumb = styled.img`
  width: 120px;
  height: 68px;
  object-fit: cover;
  border-radius: 6px;
`;
const ThumbPlaceholder = styled.div`
  width: 120px;
  height: 68px;
  border-radius: 6px;
  background: rgba(255,255,255,0.06);
`;

const A = styled.a`
  color: #9cc6ff;
  text-decoration: underline;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Btn = styled.button`
  background: #e74c3c;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 10px;
  cursor: pointer;
  &:hover { background: #c0392b; }
`;

const LoadMore = styled.button`
  margin-top: 14px;
  background: #555;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 10px 14px;
  cursor: pointer;
  &:hover { background: #666; }
`;

const Empty = styled.div`
  padding: 16px;
  color: #cfdff6;
  text-align: center;
`;

// Role label styling similar to Header RoleBadge
const RoleBadge = styled.span`
  background: ${p => p.color || '#444'};
  color: #fff;
  border-radius: 12px;
  font-weight: normal;
  padding: 2px 8px;
  font-size: 12px;
  margin-left: 8px;
`;

function roleColor(role) {
  switch (role) {
    case 'Majitel': return '#f39c12';
    case 'Admin': return '#e74c3c';
    case 'Moderátor': return '#2ecc71';
    case 'VIP': return '#e67e22';
    default: return '#3498db';
  }
}
function roleLabel(role) {
  if (role === 'User') return 'Uživatel';
  return role || 'Uživatel';
}

const OpenBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(156, 198, 255, 0.35);
  background: rgba(156, 198, 255, 0.12);
  color: #d7e7ff;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  &:hover { background: rgba(156, 198, 255, 0.2); }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10,10,12,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100001;
`;
const ConfirmBox = styled.div`
  width: min(520px, 92vw);
  background: linear-gradient(180deg,#0f1720,#11131a);
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.06);
  box-shadow: 0 20px 50px rgba(2,6,23,0.6);
  padding: 16px;
  color: #e6eef8;
`;
const ConfirmTitle = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
`;
const ConfirmText = styled.div`
  font-size: 14px;
  opacity: 0.95;
`;
const ConfirmActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 14px;
`;
const CancelBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255,255,255,0.12);
  background: transparent;
  color: #e6eef8;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
`;
const DangerBtn = styled.button`
  appearance: none;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(90deg,#ef4444,#dc2626);
  color: #fff;
  font-weight: 700;
  padding: 10px 14px;
  cursor: pointer;
`;
